import React, { useEffect, useState } from "react";
import classNames from "classnames";
import FocusTrap from 'focus-trap-react';

import Icon, { COLORS, ICONS } from "../icon";

import './modal.scss';
import "./ev-modal.scss";
import ModalPortal from "./modal-portal";
import { useCallback } from "react";

type ModalProps = {
	open: boolean;
	children: any;
	title?: string;
	onRequestClose: () => void;
	footer?: any;
	cancellable: boolean;
	closeable: boolean;
	size?: 'normal' | 'medium' | 'large' | 'small' | 'desktop' | 'tablet' | 'mobile';
	subtitle?: string;
	closeOnEscape?: boolean;
	className?: string;
	noPadding?: boolean;
	hideHeader?: boolean;
	trapFocus?: boolean;
	focusTrapOptions?: { [key: string]: string };
	showOverflow?: boolean;
	padTitle?: boolean;
	eventPreview?: boolean;
};

export default function ModalComponent(props: ModalProps): JSX.Element {
	const {
		open,
		onRequestClose,
		children,
		title = '',
		footer,
		cancellable,
		closeable,
		size = "normal",
		subtitle,
		className,
		noPadding = false,
		hideHeader = false,
		trapFocus = true,
		showOverflow = false,
		padTitle = true,
		focusTrapOptions = { allowOutsideClick: true },
		eventPreview,
	} = props;

	const [isOpen, setIsOpen] = useState(false);
	const [openClass, setOpenClass] = useState("closed");

	useEffect(() => {
		const escape = (e: any) => {
			if (e.key === 'Escape') {
				onRequestClose();
			}
		};

		if (open) {
			setIsOpen(true);
			setTimeout(() => setOpenClass("open"));
			window.addEventListener('keyup', escape);
		} else {
			setOpenClass("closed");
		}

		return () => {
			window.removeEventListener('keyup', escape);
		};
	}, [open, onRequestClose]);

	const transitionEnded = useCallback(() => {
		if (!open) {
			setIsOpen(false);
		}
	}, [open]);

	const Trap = trapFocus ? FocusTrap : React.Fragment;

	const trapProps = trapFocus ? { focusTrapOptions } : {};

	return isOpen ? (
		<ModalPortal>
			<Trap {...trapProps}>
				<div
					className={classNames(className, "modal-wrapper", openClass)}
					onTransitionEnd={transitionEnded}
					style={{ pointerEvents: 'auto' }}
				>
					<div
						onClick={cancellable ? onRequestClose : () => ({})}
						className="modal-background"
					></div>
					<div className={classNames("modal", size, { 'no-padding': noPadding }, eventPreview ? 'event-preview' : '')}>
						{hideHeader ? null : (
							<div className={classNames("modal-header", { 'pad-title': padTitle })}>
								<h4 className="modal-title">{title}</h4>
								{closeable && (
									<button
										className="no-style close"
										onClick={onRequestClose}
									>
										<Icon
											name={ICONS.CLOSE}
											size={16}
											color={COLORS.WHITE}
										/>
									</button>
								)}
								{subtitle && <p className="modal-subtitle">{subtitle}</p>}
							</div>
						)}
						<div className={`modal-body ${showOverflow ? 'show-overflow' : ''}`}>{children}</div>
						{footer && <div className="modal-footer">{footer}</div>}
					</div>
				</div>
			</Trap>
		</ModalPortal>
	) : (
		<></>
	);
}
